import { cn } from "../../../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

/**
 * Field primitives shared by the three claim submission forms.
 *
 * The classes match ConveyancePolicyConfigPage so the ESS forms and the HR
 * policy screen read as one feature rather than two.
 */

export const fieldLabelClass = "block text-xs font-semibold text-foreground mb-1.5";

export const inputBaseClass =
  "w-full h-10 px-3 rounded-lg border bg-white text-sm text-foreground focus:border-accent focus:outline-hidden focus:ring-2 focus:ring-accent/10 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed";

export const selectBaseClass =
  "h-10 rounded-lg border bg-white text-sm w-full focus:ring-accent";

export function borderFor(hasError) {
  return hasError ? "border-rose-500" : "border-(--color-text-border)";
}

export function FieldError({ message }) {
  if (!message) return null;
  return <p className="mt-1 text-[11px] font-semibold text-rose-500">{message}</p>;
}

export function FieldLabel({ children, required, htmlFor, className }) {
  return (
    <label htmlFor={htmlFor} className={cn(fieldLabelClass, className)}>
      {children}
      {required ? <span className="text-rose-500"> *</span> : null}
    </label>
  );
}

/** Label + control + error, so every field on the forms lines up identically. */
export function Field({ label, required, error, hint, htmlFor, className, children }) {
  return (
    <div className={className}>
      {label ? (
        <FieldLabel htmlFor={htmlFor} required={required}>
          {label}
        </FieldLabel>
      ) : null}
      {children}
      <FieldError message={error} />
      {hint && !error ? (
        <p className="mt-1 text-[11px] per-file-hint">{hint}</p>
      ) : null}
    </div>
  );
}

export function TextInput({ error, className, ...props }) {
  return <input className={cn(inputBaseClass, borderFor(error), className)} {...props} />;
}

/** Read-only mirror of a system-populated value (rate, auto amount, totals). */
export function ReadOnlyValue({ children, className }) {
  return (
    <div
      className={cn(
        "h-10 px-3 rounded-lg border border-(--color-text-border) bg-gray-50 text-sm per-file-hint flex items-center",
        className
      )}
    >
      {children}
    </div>
  );
}

export function SectionHint({ children, className }) {
  return (
    <p className={cn("text-[11px] text-accent", className)}>{children}</p>
  );
}

/**
 * A money input for a claim that has already chosen its currency.
 *
 * The abbreviation sits inside the field as a static prefix, not a selector:
 * a claim carries ONE metric_id, so offering the choice again beside every
 * amount on every trip line would imply a per-line currency that does not
 * exist — and let one line silently re-denominate the whole claim. The choice
 * is made once, on the claim.
 */
export function AmountField({
  value,
  onValueChange,
  abbreviation,
  disabled = false,
  readOnly = false,
  error,
  placeholder,
}) {
  return (
    <div className="relative">
      {abbreviation ? (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold per-file-hint">
          {abbreviation}
        </span>
      ) : null}
      {readOnly ? (
        <ReadOnlyValue className={abbreviation ? "pl-[52px]" : undefined}>
          {Number(value ?? 0).toFixed(2)}
        </ReadOnlyValue>
      ) : (
        <input
          type="number"
          step="0.01"
          min="0"
          inputMode="decimal"
          value={value ?? ""}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => onValueChange(event.target.value)}
          className={cn(inputBaseClass, borderFor(error), abbreviation && "pl-[52px]")}
        />
      )}
    </div>
  );
}

/**
 * An amount input with the currency selector on its left.
 *
 * The options are the metrics master's Currency rows, so the list shows
 * whatever the tenant has seeded — the selector renders each row's
 * `abbreviation`. The chosen row's `metricId` is what the claim stores in
 * metric_id, which is why the control owns both halves: an amount and the
 * currency it is denominated in always move together.
 *
 * `readOnly` renders the amount as text (auto-calculated totals, policy-driven
 * amounts) while leaving the currency selectable.
 */
export function CurrencyAmountField({
  value,
  onValueChange,
  currencies = [],
  metricId,
  onMetricChange,
  disabled = false,
  readOnly = false,
  error,
  placeholder,
  inputProps = {},
}) {
  const selected = metricId == null ? undefined : String(metricId);

  return (
    <div className="flex items-stretch gap-2">
      <div className="w-[104px] shrink-0">
        <Select
          value={selected}
          disabled={disabled || currencies.length === 0}
          onValueChange={(next) => onMetricChange(Number(next))}
        >
          <SelectTrigger className={cn(selectBaseClass, borderFor(error), "px-2")}>
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent className="max-h-60 overflow-y-auto">
            {currencies.map((currency) => (
              <SelectItem key={currency.metricId} value={String(currency.metricId)}>
                {currency.abbreviation}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {readOnly ? (
        <ReadOnlyValue className="flex-1">
          {Number(value ?? 0).toFixed(2)}
        </ReadOnlyValue>
      ) : (
        <input
          type="number"
          step="0.01"
          min="0"
          inputMode="decimal"
          value={value ?? ""}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => onValueChange(event.target.value)}
          className={cn(inputBaseClass, borderFor(error), "flex-1")}
          {...inputProps}
        />
      )}
    </div>
  );
}
